import {
  collection,
  doc,
  documentId,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import type { SongModel } from "@/models/interfaces";
import { UserTypes } from "@/models/enums";
import { songConverter } from "@/composables/useConverters";
import {
  fetchSongCatalogue,
  type SongCatalogueEntry,
} from "@/firebase/songCatalogue";
import { detectSongLanguage, type SongLanguage } from "@/utils/songLanguage";
import { countSongAuthors } from "@/utils/songFilters";

// The organisation's listed songs by what they are filtered on: title,
// author and language. The repertoire's pages come from the stored cursors,
// which know nothing of filters, so a filtered list is cut from this instead
// and only its page of songs is fetched whole (`fetchPage`).

export interface SongCatalogueState {
  /** The organisation `songs` were read for; null before they were. */
  account: number | null;
  songs: SongCatalogueEntry[];
  loading: boolean;
}

type RootState = unknown;
type Context = ActionContext<SongCatalogueState, RootState>;

// An `in` query takes at most 30 values.
const IN_LIMIT = 30;

const chunks = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size),
  );

let fetchRequest = 0;
// Organisations whose songs were given a language this session, so a write
// the rules refuse is not tried again on every visit.
const detectedAccounts = new Set<number>();

const state = (): SongCatalogueState => ({
  account: null,
  songs: [],
  loading: false,
});

const getters = {
  songs: (s: SongCatalogueState) => s.songs,
  loading: (s: SongCatalogueState) => s.loading,
  /** Whether `songs` are the active organisation's, read at least once. */
  ready: (
    s: SongCatalogueState,
    _getters: unknown,
    _rootState: unknown,
    rootGetters: any,
  ) => s.account !== null && s.account === Number(rootGetters["users/user"]?.account),
  authors: (s: SongCatalogueState) => countSongAuthors(s.songs),
};

const mutations = {
  SET_SONGS(
    s: SongCatalogueState,
    payload: { account: number | null; songs: SongCatalogueEntry[] },
  ) {
    s.account = payload.account;
    s.songs = payload.songs;
  },
  SET_LOADING(s: SongCatalogueState, value: boolean) {
    s.loading = value;
  },
  SET_LANGUAGES(
    s: SongCatalogueState,
    languages: Map<string, SongLanguage | null>,
  ) {
    for (const song of s.songs) {
      if (languages.has(song.id)) song.language = languages.get(song.id);
    }
  },
};

const actions = {
  /**
   * Reads the catalogue again. The one already read stays on screen
   * meanwhile, unless it was another organisation's.
   */
  async fetch({ state, commit, dispatch, rootGetters }: Context) {
    const user = rootGetters["users/user"];
    const account = Number(user?.account);
    if (!Number.isFinite(account)) return;
    if (state.account !== account) {
      commit("SET_SONGS", { account: null, songs: [] });
    }

    const request = ++fetchRequest;
    commit("SET_LOADING", true);
    try {
      const songs = await fetchSongCatalogue(account);
      if (request !== fetchRequest) return;
      commit("SET_SONGS", { account, songs });
    } finally {
      if (request === fetchRequest) commit("SET_LOADING", false);
    }

    // Songs saved before languages were stored get theirs from the first
    // visitor who may edit songs.
    if (Number(user?.usertype) >= UserTypes.Moderator) {
      dispatch("detectLanguages").catch((error: unknown) =>
        console.error("Detecting song languages failed:", error),
      );
    }
  },

  /**
   * Gives every song of the catalogue that has no `language` the one its
   * words are in (null when they tell none), which the song editor then
   * shows and lets be changed.
   */
  async detectLanguages({ state, commit }: Context) {
    const account = state.account;
    if (account == null || detectedAccounts.has(account)) return;
    const ids = state.songs
      .filter((song) => song.language === undefined)
      .map((song) => song.id);
    if (!ids.length) return;
    detectedAccounts.add(account);

    for (const group of chunks(ids, IN_LIMIT)) {
      const snap = await getDocs(
        query(collection(db, "songs"), where(documentId(), "in", group)),
      );
      const languages = new Map<string, SongLanguage | null>();
      const batch = writeBatch(db);
      for (const song of snap.docs) {
        const data = song.data();
        // Saved in the meantime, by an editor that sets it.
        if (data.language !== undefined || Number(data.account) !== account) continue;
        const language = detectSongLanguage(data as SongModel);
        languages.set(song.id, language);
        batch.update(doc(db, "songs", song.id), { language });
      }
      if (!languages.size) continue;
      await batch.commit();
      if (state.account === account) commit("SET_LANGUAGES", languages);
    }
  },

  /** Whole songs by id, in the order asked for; missing ones left out. */
  async fetchPage(
    _: Context,
    ids: string[],
  ): Promise<(SongModel & { id: string })[]> {
    const groups = await Promise.all(
      chunks(ids, IN_LIMIT).map((group) =>
        getDocs(
          query(
            collection(db, "songs").withConverter(songConverter),
            where(documentId(), "in", group),
          ),
        ),
      ),
    );
    const byId = new Map(
      groups.flatMap((snap) =>
        snap.docs.map((d) => [d.id, { ...d.data(), id: d.id }] as const),
      ),
    );
    return ids.flatMap((id) => byId.get(id) ?? []);
  },
};

export const songCatalogue: Module<SongCatalogueState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default songCatalogue;
